/**
 * Development-only same-origin proxy addressing.
 *
 * WHY THIS EXISTS (2026-09-03)
 * ----------------------------
 * The app derives its API origin from the page host (see `ApiConfig`), and the
 * backends only allow the deployed site origins. Measured 2026-09-03:
 *
 *     OPTIONS https://qa-api-v1.xconvert.com/v1/api/settings
 *     Origin: http://localhost:3000
 *     -> Access-Control-Allow-Origin: https://www.xconvert.com
 *
 * so every API call made from a `next dev` server is blocked by the browser and
 * the dev server is useless as an inner loop — every UI change had to go through
 * a 4-6 minute QA build.
 *
 * The fix is to stop making cross-origin calls at all in development: the dev
 * server proxies the backend paths itself (see `rewrites()` in next.config.js),
 * and this module is the ONE place that decides whether the app addresses those
 * backends by absolute origin (deployed) or by a relative path (dev). Nothing
 * else in the app branches on it: `ApiConfig` asks these two functions, and
 * every endpoint helper in the codebase is already built on top of `ApiConfig`.
 *
 * Never active in a deployed build: `next build` runs with NODE_ENV=production
 * and no `NEXT_PUBLIC_XC_DEV_PROXY`, so `isDevProxyEnabled()` is false and the
 * exported values are never consulted.
 */

/**
 * Prefix for host-addressed proxying: `/__xcdev/h/<host>/<path>`.
 *
 * A prefix that carries the host is needed (rather than a plain path rewrite)
 * because the edge and pod backends BOTH serve `/v2/edge/*` and the edge host is
 * chosen at runtime by `/v1/api/servers/resolve`, so the path alone cannot say
 * which machine a request belongs to. next.config.js maps the prefix back to
 * `https://<host>/<path>` and constrains `<host>` to the QA name-space.
 */
export const DEV_PROXY_HOST_PREFIX = '/__xcdev/h';

/**
 * True when the app should address backends through the dev server's own origin.
 *
 * `NEXT_PUBLIC_XC_DEV_PROXY` is read as a full member expression on purpose —
 * that is the form Next inlines into the client bundle at build time. Explicit
 * `1`/`0` wins; otherwise it follows NODE_ENV, so a plain `next dev` gets a
 * working API too and `next build` / jest (`NODE_ENV=test`) never do.
 */
export function isDevProxyEnabled(): boolean {
    const explicit = process.env.NEXT_PUBLIC_XC_DEV_PROXY;
    if (explicit === '1') {
        return true;
    }
    if (explicit === '0') {
        return false;
    }
    return process.env.NODE_ENV === 'development';
}

/**
 * The same-origin base path that stands in for `https://<host>` while the dev
 * proxy is on. Accepts a bare host (`qa-1.xconvert.com`) or an already-qualified
 * one, matching `UrlUtils.toHttpsProtocol`, whose place it takes.
 */
export function devProxyOrigin(host: string): string {
    const bare = String(host || '').replace(/^.*?:\/\//i, '').replace(/\/+$/, '');
    return `${DEV_PROXY_HOST_PREFIX}/${bare}`;
}
